import type { Receipt } from '@/model';

import { FC, useEffect, useMemo, useState } from 'react';

import { Modal } from '../molecules';
import { BottomSheet } from '../molecules/BottomSheet';

type MedicineFilterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  receipts: Receipt[];
  selectedMedicineKeys: string[];
  onSelectMedicines: (medicineKeys: string[]) => void;
};

export const MedicineFilterModal: FC<MedicineFilterModalProps> = ({
  isOpen,
  onClose,
  receipts,
  selectedMedicineKeys,
  onSelectMedicines,
}) => {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 768px)').matches;
  });

  const [search, setSearch] = useState<string>('');
  const [draftSelectedKeys, setDraftSelectedKeys] =
    useState<string[]>(selectedMedicineKeys);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    // set initial
    setIsDesktop(mql.matches);
    // subscribe
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // safari fallback
    // @ts-expect-error legacy
    mql.addListener(handler);
    // @ts-expect-error legacy
    return () => mql.removeListener(handler);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setDraftSelectedKeys(selectedMedicineKeys);
      setSearch('');
    }
  }, [isOpen, selectedMedicineKeys]);

  const medicines = useMemo(() => {
    const medicineMap = new Map<
      string,
      { key: string; name: string; count: number }
    >();

    const getKey = (m: { id?: number | null; name: string }) => {
      if (m.id !== null && m.id !== undefined) return `id:${m.id}`;
      return `name:${m.name}`;
    };

    receipts.forEach((receipt) => {
      receipt.receipt_medicine.forEach((rm) => {
        const key = getKey(rm.medicine);
        if (medicineMap.has(key)) {
          const existing = medicineMap.get(key)!;
          medicineMap.set(key, {
            ...existing,
            count: existing.count + 1,
          });
        } else {
          medicineMap.set(key, {
            key,
            name: rm.medicine.name,
            count: 1,
          });
        }
      });
    });

    return Array.from(medicineMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [receipts]);

  const apply = () => {
    onSelectMedicines(draftSelectedKeys);
    onClose();
  };

  const reset = () => {
    setDraftSelectedKeys([]);
    setSearch('');
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filteredMedicines = useMemo(() => {
    if (!normalizedSearch) return medicines;
    return medicines.filter((m) =>
      m.name.toLowerCase().includes(normalizedSearch),
    );
  }, [medicines, normalizedSearch]);

  const selectedSet = useMemo(
    () => new Set(draftSelectedKeys),
    [draftSelectedKeys],
  );
  const selectedMedicines = useMemo(
    () => filteredMedicines.filter((m) => selectedSet.has(m.key)),
    [filteredMedicines, selectedSet],
  );
  const unselectedMedicines = useMemo(
    () => filteredMedicines.filter((m) => !selectedSet.has(m.key)),
    [filteredMedicines, selectedSet],
  );

  const toggle = (key: string) => {
    setDraftSelectedKeys((prev) => {
      if (prev.includes(key)) return prev.filter((x) => x !== key);
      return [...prev, key];
    });
  };

  const SearchIcon = (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.95833 15.8333C12.7553 15.8333 15.8333 12.7553 15.8333 8.95833C15.8333 5.16138 12.7553 2.08333 8.95833 2.08333C5.16138 2.08333 2.08333 5.16138 2.08333 8.95833C2.08333 12.7553 5.16138 15.8333 8.95833 15.8333Z"
        stroke="#151515"
        strokeOpacity="0.4"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.9167 17.9167L14.1667 14.1667"
        stroke="#151515"
        strokeOpacity="0.4"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const Row = ({
    medicine,
    checked,
  }: {
    medicine: { key: string; name: string; count: number };
    checked: boolean;
  }) => (
    <button
      type="button"
      onClick={() => toggle(medicine.key)}
      className={`flex w-full items-center gap-[14px] rounded-[16px] p-[12px] text-left transition-colors md:px-[16px] md:py-[14px] ${
        checked ? 'bg-[#f1f7fa]' : 'hover:bg-[#f7f8f9]'
      }`}
    >
      <span
        className={`flex size-[24px] items-center justify-center rounded-[50%] border ${
          checked
            ? 'border-[#0788f1] bg-[#0788f1]'
            : 'border-[#151515]/30 bg-white'
        }`}
      >
        {checked ? (
          <svg
            width="14"
            height="10"
            viewBox="0 0 14 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.5 5.5L5 8.5L12.5 1.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </span>
      <span className="text-[1.25rem] font-normal leading-7 text-[#151515]">
        {medicine.name}
      </span>
    </button>
  );

  const content = (
    <div className="md:p-[24px]">
      <div className="mb-[16px]">
        <div className="text-[16px] font-normal leading-9 text-[#151515]/50 ">
          Препараты
        </div>
      </div>

      <div className="relative mb-[16px]">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск"
          className="h-[56px] w-full rounded-[16px] border border-[#151515]/20 bg-white px-[18px] pr-[54px] text-[1.25rem] font-normal leading-7 text-[#151515] outline-none focus:border-[#0788f1]"
        />
        <div className="top-[10px]/2 pointer-events-none absolute right-[18px] -translate-y-1/2">
          {SearchIcon}
        </div>
      </div>

      <div className="max-h-[50vh] overflow-y-auto">
        {selectedMedicines.length > 0 && (
          <div className="rounded-[20px] bg-[#f1f7fa]">
            {selectedMedicines.map((m) => (
              <Row key={m.key} medicine={m} checked />
            ))}
          </div>
        )}

        <div className="flex flex-col">
          {unselectedMedicines.map((m) => (
            <Row key={m.key} medicine={m} checked={false} />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      {isDesktop ? (
        <Modal isOpen={isOpen} onClose={onClose} panelClassName="!w-fit">
          {content}
          <div className="flex justify-center gap-[12px] px-6 pb-6">
            <button
              type="button"
              className="flex h-[48px] w-full items-center justify-center rounded-[12px] bg-[#f1f7fa] px-[16px] text-[1rem] font-bold leading-5 text-[#151515]"
              onClick={() => {
                onSelectMedicines([]);
                onClose();
              }}
            >
              Сбросить
            </button>
            <button
              type="button"
              className="flex h-[48px] w-full items-center justify-center rounded-[12px] bg-[#0788f1] px-[16px] text-[1rem] font-bold leading-5 text-white"
              onClick={() => {
                onSelectMedicines(draftSelectedKeys);
                onClose();
              }}
            >
              Применить
            </button>
          </div>
        </Modal>
      ) : (
        <BottomSheet isOpen={isOpen} onClose={onClose} title="Фильтры">
          {content}
          <div className="mt-[32px] flex w-full gap-[12px]">
            <button
              type="button"
              onClick={reset}
              className="flex flex-1 items-center justify-center rounded-[12px] bg-[#f1f7fa] p-[20px] text-[16px] font-bold leading-5 text-[#151515]"
            >
              Сбросить
            </button>
            <button
              type="button"
              onClick={apply}
              className="flex flex-1 items-center justify-center rounded-[12px] bg-[#0788f1] p-[20px] text-[16px] font-bold leading-5 text-white"
            >
              Применить
            </button>
          </div>
        </BottomSheet>
      )}
    </>
  );
};
